// LeadWix 2.0 — Feature flags helper
// Resolves feature access from sessionStorage authUser, falling back to
// SOLO defaults if nothing is stored. The backend `/api/feature-flags`
// endpoint is the source of truth; this module mirrors that resolution
// for fast client-side gating.

import { APIClient } from "./api_helper";

const api = new APIClient();

const SOLO_DEFAULTS = {
  REMINDERS: true,
  CUSTOMERS: true,
  WHATSAPP_INBOX: true,
  PIPELINE: false,
  LEADS: false,
  LEAD_WIDGET: false,
  SEQUENCES: false,
  TEAM: false,
  ROUTING_RULES: false,
  AI_DRAFTS: false,
  AI_SUMMARIZER: false,
  REPORTS: false,
  WHITE_LABEL: false,
};

const PROFESSIONAL_DEFAULTS = {
  ...SOLO_DEFAULTS,
  PIPELINE: true,
  LEADS: true,
  LEAD_WIDGET: true,
  SEQUENCES: true,
  AI_DRAFTS: true,
  AI_SUMMARIZER: true,
  REPORTS: true,
};

const TEAM_DEFAULTS = {
  ...PROFESSIONAL_DEFAULTS,
  TEAM: true,
  ROUTING_RULES: true,
  WHITE_LABEL: true,
};

export const FEATURES = {
  REMINDERS: "REMINDERS",
  CUSTOMERS: "CUSTOMERS",
  WHATSAPP_INBOX: "WHATSAPP_INBOX",
  PIPELINE: "PIPELINE",
  LEADS: "LEADS",
  LEAD_WIDGET: "LEAD_WIDGET",
  SEQUENCES: "SEQUENCES",
  TEAM: "TEAM",
  ROUTING_RULES: "ROUTING_RULES",
  AI_DRAFTS: "AI_DRAFTS",
  AI_SUMMARIZER: "AI_SUMMARIZER",
  REPORTS: "REPORTS",
  WHITE_LABEL: "WHITE_LABEL",
};

const readAuthUser = () => {
  try {
    const raw = sessionStorage.getItem("authUser");
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeAuthUser = (next) => {
  try {
    sessionStorage.setItem("authUser", JSON.stringify(next));
    // Notify same-tab subscribers (storage event only fires cross-tab)
    window.dispatchEvent(new Event("authUserChanged"));
  } catch (e) {
    /* noop */
  }
};

const defaultsForMode = (mode) => {
  switch ((mode || "SOLO").toUpperCase()) {
    case "TEAM":
      return TEAM_DEFAULTS;
    case "PROFESSIONAL":
      return PROFESSIONAL_DEFAULTS;
    default:
      return SOLO_DEFAULTS;
  }
};

/**
 * Map any known plan/planType string to a numeric tier:
 *   0 = SOLO, 1 = PROFESSIONAL, 2 = TEAM
 * Mirrors backend FeatureGateService#planTier so client-side gating
 * matches the server until /feature-flags refresh completes.
 */
const planTier = (plan) => {
  if (!plan) return 0;
  const p = String(plan).trim().toUpperCase();
  if (!p || p === "FREE") return 0;
  if (p.includes("TEAM") || p === "BUSINESS" || p === "ENTERPRISE") return 2;
  if (
    p.includes("PROFESSIONAL") || p.includes("PRO") ||
    p === "YEARLY" || p === "MONTHLY" || p === "BASIC"
  ) return 1;
  return 0;
};

/** Resolve effective mode from userMode + paid plan + active trial. */
const resolveEffectiveMode = (user) => {
  if (!user) return "SOLO";
  let tier = 0;
  const mode = (user.userMode || "").toUpperCase();
  if (mode === "TEAM") tier = Math.max(tier, 2);
  else if (mode === "PROFESSIONAL") tier = Math.max(tier, 1);

  tier = Math.max(tier, planTier(user.plan));
  tier = Math.max(tier, planTier(user.planType));

  // Active trial — backend sets trialEndDate; treat any non-expired trial as ≥ PROFESSIONAL
  const trialEnd = user.trialEndDate ? new Date(user.trialEndDate) : null;
  const trialActive = trialEnd && trialEnd.getTime() > Date.now();
  if (trialActive) {
    tier = Math.max(tier, planTier(user.trialPlan), 1);
  }

  if (tier === 2) return "TEAM";
  if (tier === 1) return "PROFESSIONAL";
  return "SOLO";
};

/** Get the resolved feature flag map for the current user. */
export const getFeatureFlags = () => {
  const user = readAuthUser();
  if (!user) return SOLO_DEFAULTS;
  const baseline = defaultsForMode(resolveEffectiveMode(user));
  const overrides = user.resolvedFeatureFlags || {};
  return { ...baseline, ...overrides };
};

/** Quick check for a single feature. */
export const hasFeature = (feature) => {
  const flags = getFeatureFlags();
  return Boolean(flags[feature]);
};

/** Get current userMode (SOLO|PROFESSIONAL|TEAM). */
export const getUserMode = () => {
  const user = readAuthUser();
  return resolveEffectiveMode(user);
};

/** Whether onboarding has been completed. */
export const isOnboardingCompleted = () => {
  const user = readAuthUser();
  // Default to true for legacy users (no flag) so we don't lock them out
  return user?.onboardingCompleted !== false;
};

/**
 * Refresh feature flags from backend and persist to authUser.
 * Call after login + after onboarding completion.
 */
export const refreshFeatureFlags = async () => {
  try {
    const data = await api.get("/feature-flags");
    const user = readAuthUser() || {};
    const next = {
      ...user,
      userMode: data.userMode || user.userMode || "SOLO",
      workspaceId: data.workspaceId || user.workspaceId,
      resolvedFeatureFlags: data.featureFlags || {},
      aiTokensRemaining: data.aiTokensRemaining ?? user.aiTokensRemaining ?? 0,
      // LeadWix 2.0 — keep plan + trial in sync so client gating matches server
      plan: data.plan ?? user.plan,
      planType: data.planType ?? user.planType,
      trialPlan: data.trialPlan ?? user.trialPlan,
      trialEndDate: data.trialEndDate ?? user.trialEndDate,
      trialActive: data.trialActive ?? user.trialActive,
      paymentStatus: data.paymentStatus ?? user.paymentStatus,
      gracePeriodUntil: data.gracePeriodUntil ?? user.gracePeriodUntil,
      planEndDate: data.planEndDate ?? user.planEndDate,
    };
    writeAuthUser(next);
    return next;
  } catch (e) {
    console.warn("refreshFeatureFlags failed:", e);
    return null;
  }
};
